import React, { useMemo } from "react";
import { ListFilter, X } from "lucide-react";
import { RoomCategoryFilters } from "@/types/roomCategory";
import { useRoomCategoriesFiltersSettings } from "@/hooks/useRoomCategories";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const ALL_CATEGORIES = "all categories";

const FILTER_CHECKBOX_CLASS =
  "size-5 rounded-[4px] border-[#D5D7DA] data-[state=checked]:border-[#30344A] data-[state=checked]:bg-[#30344A] data-[state=checked]:text-white";

const RoomCategoriesFilter = ({
  filters,
  actions,
  hotelId,
}: {
  hotelId: string;
  filters: RoomCategoryFilters;
  actions: {
    setCategoryId: (value: string) => void;
    setAmenities: (value: string[]) => void;
    handleFilterChange: <T>(setter: (value: T) => void, value: T) => void;
  };
}) => {
  const { data: settings, isPending } =
    useRoomCategoriesFiltersSettings(hotelId);
  const [open, setOpen] = React.useState(false);
  const appliedCategoryId = filters.categoryId || ALL_CATEGORIES;
  const appliedAmenities = useMemo(
    () => filters.amenities || [],
    [filters.amenities],
  );
  const [draftCategoryId, setDraftCategoryId] =
    React.useState<string>(appliedCategoryId);
  const [draftAmenities, setDraftAmenities] =
    React.useState<string[]>(appliedAmenities);

  const categoryOptions = useMemo(
    () => [
      { value: ALL_CATEGORIES, label: "All categories" },
      ...(settings?.categories.map((category) => ({
        value: category.id,
        label: category.name,
      })) || []),
    ],
    [settings],
  );
  const amenitiesOptions = useMemo(
    () => settings?.amenities.map((a) => ({ value: a, label: a })) || [],
    [settings],
  );

  if (isPending) {
    return null;
  }

  const appliedCount =
    (appliedCategoryId !== ALL_CATEGORIES ? 1 : 0) + appliedAmenities.length;
  const draftCount =
    (draftCategoryId !== ALL_CATEGORIES ? 1 : 0) + draftAmenities.length;

  const toggleAmenity = (value: string) =>
    setDraftAmenities((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );

  const apply = (categoryId: string, amenities: string[]) => {
    actions.handleFilterChange(actions.setCategoryId, categoryId);
    actions.handleFilterChange(actions.setAmenities, amenities);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setDraftCategoryId(appliedCategoryId);
          setDraftAmenities(appliedAmenities);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="icon-text"
          className="text-foreground h-8 min-h-8 w-fit gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
        >
          <ListFilter className="size-4" />
          {appliedCount > 0 ? `Filters (${appliedCount})` : "Filters"}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="flex max-h-[min(560px,calc(100vh-120px))] w-[320px] flex-col gap-4 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        <div className="flex shrink-0 items-start justify-between gap-2">
          <h4 className="text-base leading-normal font-semibold text-[#171717]">
            {draftCount > 0 ? `Filters (${draftCount})` : "Filters"}
          </h4>
          <Button
            type="button"
            variant="span"
            className="rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
            onClick={() => setOpen(false)}
            aria-label="Close filters"
          >
            <X className="size-5" />
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-0.5">
          <fieldset className="space-y-1">
            <legend className="mb-1 text-sm font-medium text-[#171717]">
              Categories
            </legend>
            {categoryOptions.map((option) => {
              const checked = draftCategoryId === option.value;
              return (
                <label
                  key={option.value}
                  className={cn(
                    "flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5",
                    checked ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]",
                  )}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => setDraftCategoryId(option.value)}
                    className={FILTER_CHECKBOX_CLASS}
                  />
                  <span className="text-sm leading-normal font-normal text-[#171717]">
                    {option.label}
                  </span>
                </label>
              );
            })}
          </fieldset>

          <fieldset className="space-y-1">
            <legend className="mb-1 text-sm font-medium text-[#171717]">
              Features
            </legend>
            {amenitiesOptions.map((option) => {
              const checked = draftAmenities.includes(option.value);
              return (
                <label
                  key={option.value}
                  className={cn(
                    "flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5",
                    checked ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]",
                  )}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleAmenity(option.value)}
                    className={FILTER_CHECKBOX_CLASS}
                  />
                  <span className="text-sm leading-normal font-normal text-[#171717]">
                    {option.label}
                  </span>
                </label>
              );
            })}
          </fieldset>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-8 rounded-md border-[#E5E5E5] bg-white px-4 text-sm font-medium text-zinc-900 shadow-none hover:bg-zinc-50"
            onClick={() => {
              setDraftCategoryId(ALL_CATEGORIES);
              setDraftAmenities([]);
              apply(ALL_CATEGORIES, []);
            }}
          >
            Reset
          </Button>
          <Button
            type="button"
            className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm leading-normal font-medium text-[#FAFAFA] hover:bg-[#5F7597]"
            onClick={() => {
              apply(draftCategoryId, draftAmenities);
              setOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default RoomCategoriesFilter;
